import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ImageUp, Loader, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { isDiferentValues, urlToFile } from "@/App/Tenants/Update";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { colors } from "@/utils/colors";
import {
  getShowUserTenant,
  updateUserUnities,
  getUsersTenantData,
} from "@/Api/unities";
import { getSelectRoles } from "@/Api/roles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form as FormComponent } from "@/components/Form";
import { Mask } from "@/utils/mask";

export const UpdateUserTenantBtn = ({ updateFront, uuid, uuidUnit }) => {
  const [originalData, setOriginalData] = useState({});
  const [roles, setRoles] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [state, setState] = useState("fetch");

  const form = useForm();
  const formWatch = form.watch();

  const handleOpenUpdate = async () => {
    const { data: responseApi, ok } = await getShowUserTenant(uuidUnit, uuid);
    if (!ok) {
      setIsOpen((prev) => !prev);
      toast("Error ao buscar dados");
      return;
    }

    // Fetch roles
    try {
      const { data } = await getSelectRoles();
      setRoles(data?.response || []);
    } catch (error) {
      console.error("Erro ao buscar roles:", error);
    }

    ["name", "email", "photo", "password", "document", "phone"].forEach(
      async (valueKey) => {
        const responseValue = responseApi?.response?.user?.[valueKey];

        if (valueKey === "password") {
          form.setValue(valueKey, "");
          setOriginalData((prev) => ({
            ...prev,
            [valueKey]: "",
          }));
        } else if (valueKey === "phone") {
          const maskedPhone = responseValue
            ? Mask.maskPhone(responseValue)
            : "";
          form.setValue(valueKey, maskedPhone);
          setOriginalData((prev) => ({
            ...prev,
            [valueKey]: maskedPhone,
          }));
        } else {
          form.setValue(valueKey, responseValue);
          setOriginalData((prev) => ({
            ...prev,
            [valueKey]: responseValue,
          }));
        }
      }
    );

    if (
      responseApi?.response?.user?.roles &&
      responseApi?.response?.user?.roles.length > 0
    ) {
      const roleId = responseApi.response.user.roles[0].id;
      form.setValue("role_id", String(roleId));
      setOriginalData((prev) => ({
        ...prev,
        role_id: String(roleId),
      }));
    } else {
      form.setValue("role_id", "");
      setOriginalData((prev) => ({
        ...prev,
        role_id: "",
      }));
    }

    setState("done");
    setIsOpen(true);
  };

  const formSubmit = async (data) => {
    setIsLoad((prev) => !prev);
    setIsEdited(false);
    const roles: string[] = [];
    if (data?.role_id) {
      roles.push(String(data?.role_id));
    }
    const payload = {
      ...data,
      photo:
        typeof data?.photo === "string"
          ? await urlToFile(data?.photo)
          : data?.photo?.[0] || null,
      roles: roles,
    };

    const { data: apiResponse, ok } = await updateUserUnities(
      uuidUnit,
      uuid,
      payload
    );
    if (!ok) {
      toast("Error ao atualizar o usuário!");
      setIsLoad((prev) => !prev);
      setIsOpen(false);
      return;
    }

    const { data: refreshData } = await getUsersTenantData(uuidUnit);
    updateFront(refreshData?.response);

    toast(apiResponse?.message);
    setIsLoad((prev) => !prev);
    setIsOpen(false);
  };

  useEffect(() => {
    isDiferentValues(formWatch, originalData)
      ? setIsEdited(true)
      : setIsEdited(false);
  }, [
    formWatch.name,
    formWatch.email,
    formWatch.photo,
    formWatch.password,
    formWatch.document,
    formWatch.phone,
    formWatch.role_id,
  ]);

  useEffect(() => {
    if (!isOpen) form.reset();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={handleOpenUpdate}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Atualizar Usuário
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-3" onSubmit={form.handleSubmit(formSubmit)}>
            <FormItem className="flex flex-col items-center gap-1">
              <FormLabel
                className={cn(
                  "flex gap-2",
                  state === "fetch" && "animate-pulse"
                )}
              >
                Perfil:
              </FormLabel>
              <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                <div className="flex text-sm/6 text-gray-600 h-full w-full">
                  <label
                    htmlFor="file-upload"
                    className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                  >
                    {formWatch?.photo && formWatch?.photo?.length > 0 ? (
                      typeof formWatch?.photo === "string" ? (
                        <img
                          className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-50 transition rounded-full"
                          src={formWatch?.photo}
                          alt="profile_image"
                        />
                      ) : (
                        <img
                          className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                          src={URL.createObjectURL(formWatch?.photo?.[0])}
                          alt="profile_image"
                        />
                      )
                    ) : (
                      ""
                    )}
                    <span className="flex gap-2 items-center relative z-50">
                      {!(formWatch?.photo?.length > 0) ? (
                        <ImageUp className="size-7 group-hover:text-blue-700" />
                      ) : (
                        <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                      )}
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onBlurCapture={(e) => e.stopPropagation()}
                      {...form.register("photo")}
                    />
                  </label>
                </div>
              </div>
            </FormItem>
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Campo Obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={cn(state === "fetch" && "animate-pulse")}
                  >
                    Nome:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        state === "fetch" ? "Carregando..." : "Insira um nome"
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: "Campo Obrigatório",
                pattern: { value: /^\S+@\S+$/i, message: "Formato inválido." },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={cn(state === "fetch" && "animate-pulse")}
                  >
                    E-mail:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        state === "fetch" ? "Carregando..." : "Insira o e-mail"
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Senha:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="password"
                      placeholder="Deseja atualizar senha?"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="document"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={cn(state === "fetch" && "animate-pulse")}
                  >
                    Documento:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="cpfOrCnpj"
                      placeholder={
                        state === "fetch"
                          ? "Carregando..."
                          : "Insira o documento"
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={cn(state === "fetch" && "animate-pulse")}
                  >
                    Telefone:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="phone"
                      placeholder={
                        state === "fetch"
                          ? "Carregando..."
                          : "Insira o telefone"
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormComponent.Field name="role_id">
              {({ field }) => (
                <FormComponent.Item>
                  <FormComponent.Label
                    className={cn(state === "fetch" && "animate-pulse")}
                  >
                    Cargo <FormComponent.Message />
                  </FormComponent.Label>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormComponent.Control>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um Cargo" />
                      </SelectTrigger>
                    </FormComponent.Control>
                    <SelectContent>
                      {roles.map((item: any) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormComponent.Item>
              )}
            </FormComponent.Field>
            <DialogFooter className="mt-5">
              <Button
                type="submit"
                className={cn("flex gap-2 mt-1", colors.bg_primary)}
                disabled={!isEdited}
              >
                {isLoad ? (
                  <>
                    <Loader className="animate-spin size-4" /> Atualizando
                  </>
                ) : (
                  <>Atualizar</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
